// CONTRACT:C3-CHAT.1.0
// COP — the per-message "trust made visible" card (React shell only; never enters the render loop).
//
// A DEMO-TIER, DISPLAY/CLIENT-CRYPTO-ONLY viewer opened by clicking a chat message. It shows:
//   (1) the SIGNER (callsign + id) and a FAIL-CLOSED verify badge — "signed (demo-tier,
//       client-verified)" / "asserted — not signed" / "SIGNATURE INVALID". The verify runs
//       client-side over the SEAL-BOUND input (verifyMessageSignature): a signature that does
//       not bind THIS seal + id|room|sender can NEVER read as "signed".
//   (2) the signature (truncated hex + copy), the pubkey + kid (RFC 7638-shaped).
//   (3) the RAW ZTDF/nanoTDF manifest ALREADY in the sealed body (keyAccess entries, policy
//       binding, integrityInformation/segments, the KAS url, the wrapping, assertions) — the
//       "the fabric never sees what it carries" moment: the manifest describes the envelope; the
//       body ciphertext (0.payload) is NOT here in cleartext. Clear-bridge / unparseable states
//       are surfaced HONESTLY, never faked.
//
// HONESTY DISCIPLINE (mandatory): everything is labelled DEMO-TIER / client-verified until
// TDFLite lands the independent kernel-grade verify seam. This card NEVER decrypts, never asks
// the KAS, and is never an identity/authorization input — it reads the same bytes a DENIED reader
// already holds, so it works even for a sealed/redacted row.

import { useEffect, useMemo, useState } from "react";
import type { ChatMessage } from "../capabilities/chatCapability.ts";
import {
  verifyMessageSignature,
  parseZtdfManifest,
  type SignatureVerifyState,
  type ZtdfManifestView,
} from "../../../../packages/cap-chat/src/messages.ts";
import { classLabel } from "./chatVocab.ts";
import { pushToast } from "./Toasts.tsx";

/** Copy text to the clipboard with a toast ack (fail-soft: a clipboard-blocked env just no-ops). */
function copy(label: string, text: string): void {
  void navigator.clipboard
    ?.writeText(text)
    .then(() => pushToast({ title: "Copied", body: label, tone: "success" }))
    .catch(() => pushToast({ title: "Copy failed", body: "clipboard unavailable", tone: "alert" }));
}

/** Middle-truncate a long token for display ("abcd…wxyz"). */
function trunc(s: string, head = 10, tail = 8): string {
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

/** base64url → lowercase hex (for the "cryptographic" signature/pubkey display). */
function b64UrlToHex(s: string): string {
  try {
    const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
    const bin = atob(b64);
    let h = "";
    for (let i = 0; i < bin.length; i++) h += bin.charCodeAt(i).toString(16).padStart(2, "0");
    return h;
  } catch {
    return s; // not decodable — show as-is (never throw in a display path)
  }
}

/** The three fail-closed verdict states → glyph + label + honest sublabel. */
const VERDICT: Record<SignatureVerifyState, { glyph: string; label: string; sub: string }> = {
  signed: {
    glyph: "✅",
    label: "Signed",
    sub: "demo-tier, client-verified — the signature binds THIS seal + id·room·sender",
  },
  asserted: {
    glyph: "⚠️",
    label: "Asserted — not signed",
    sub: "no client signature — sender id is asserted by the sending client (the honest trust line)",
  },
  invalid: {
    glyph: "❌",
    label: "Signature INVALID",
    sub: "a signature is present but FAILS verification over this seal — do not trust it",
  },
};

/** A labelled key/value row; `mono` renders the value monospace, `onCopy` adds a copy button. */
function Field({
  label,
  value,
  mono,
  onCopy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy?: () => void;
}): JSX.Element {
  return (
    <div className="chat__trust-field">
      <span className="chat__trust-field-key">{label}</span>
      <span className={`chat__trust-field-val${mono ? " chat__trust-field-val--mono" : ""}`}>{value}</span>
      {onCopy ? (
        <button type="button" className="chat__trust-copy" title={`Copy ${label}`} onClick={onCopy}>
          copy
        </button>
      ) : null}
    </div>
  );
}

/** The trust card modal. `msg` carries the (client-asserted) signature block + the sealed
 *  `encryptedRef`; `displayName` is the callsign-first sender name (from the panel). */
export function MessageTrustCard({
  msg,
  displayName,
  onClose,
}: {
  msg: ChatMessage;
  displayName: string;
  onClose: () => void;
}): JSX.Element {
  const sig = msg.signature;

  // FAIL-CLOSED verify. Default "asserted" until the async WebCrypto verify resolves — the card
  // NEVER shows "signed" before verification proves it over the seal-bound input.
  const [state, setState] = useState<SignatureVerifyState>("asserted");
  useEffect(() => {
    let alive = true;
    void verifyMessageSignature({
      signature: sig,
      sealMaterial: msg.encryptedRef,
      id: msg.id,
      room: msg.room,
      sender: msg.author,
    }).then((s) => {
      if (alive) setState(s);
    });
    return () => {
      alive = false;
    };
  }, [sig, msg.encryptedRef, msg.id, msg.room, msg.author]);

  // PURE parse of the sealed envelope (no crypto/KAS/decrypt) — works even for a redacted row.
  const view: ZtdfManifestView = useMemo(() => parseZtdfManifest(msg.encryptedRef), [msg.encryptedRef]);
  const manifestJson = useMemo(
    () => (view.manifest ? JSON.stringify(view.manifest, null, 2) : ""),
    [view.manifest],
  );

  const verdict = VERDICT[state];

  // ESC closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="chat__modal-scrim" role="presentation" onClick={onClose}>
      <div
        className="chat__trustcard"
        role="dialog"
        aria-modal="true"
        aria-label="Message trust details"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat__trust-head">
          <span className="chat__trust-title">Message trust</span>
          <span className="chat__trust-demotag" title="Client-side signature + client-side manifest parse — until TDFLite lands the independent kernel-grade verify seam">
            DEMO-TIER · client-verified
          </span>
          <button type="button" className="chat__trust-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* ── FAIL-CLOSED VERIFY BADGE ── */}
        <div className={`chat__trust-verdict chat__trust-verdict--${state}`} role="status">
          <span className="chat__trust-verdict-glyph" aria-hidden="true">{verdict.glyph}</span>
          <span className="chat__trust-verdict-main">
            <span className="chat__trust-verdict-label">{verdict.label}</span>
            <span className="chat__trust-verdict-sub">{verdict.sub}</span>
          </span>
        </div>

        {/* ── SIGNER ── */}
        <div className="chat__trust-section">
          <div className="chat__trust-section-title">Signer</div>
          <Field label="callsign" value={displayName} />
          <Field label="id" value={msg.author} />
          {sig ? (
            <>
              <Field label="alg" value={sig.alg === "Ed25519" ? "Ed25519 (EdDSA)" : "ECDSA P-256"} />
              <Field label="kid" value={trunc(sig.kid, 12, 8)} mono onCopy={() => copy("kid", sig.kid)} />
              <Field label="pubkey" value={trunc(b64UrlToHex(sig.pub), 12, 10)} mono onCopy={() => copy("pubkey (base64url)", sig.pub)} />
              <Field label="signature" value={trunc(b64UrlToHex(sig.sig), 14, 10)} mono onCopy={() => copy("signature (base64url)", sig.sig)} />
              {sig.sealDigest ? (
                <Field label="binds seal" value={trunc(sig.sealDigest, 14, 10)} mono onCopy={() => copy("seal digest (SHA-256)", sig.sealDigest!)} />
              ) : null}
              <div className="chat__trust-hint">
                RFC 7638-shaped kid; the future registered-key home is idplite delegation keygen.
              </div>
            </>
          ) : (
            <div className="chat__trust-hint">
              This message carries no client signature — sender identity is <strong>asserted</strong> by the sending
              client, not message-signed (the honest current state).
            </div>
          )}
        </div>

        {/* ── RAW ZTDF MANIFEST (the wow) ── */}
        <div className="chat__trust-section">
          <div className="chat__trust-section-title">
            Sealed envelope · raw ZTDF manifest
            {view.kind === "sealed" ? (
              <button type="button" className="chat__trust-copy chat__trust-copy--right" title="Copy manifest JSON" onClick={() => copy("manifest JSON", manifestJson)}>
                copy JSON
              </button>
            ) : null}
          </div>

          {view.kind === "sealed" ? (
            <>
              <div className="chat__trust-highlights">
                {view.kasUrl ? <Field label="KAS" value={view.kasUrl} mono /> : null}
                {view.encryptionAlgorithm ? <Field label="wrapping" value={view.encryptionAlgorithm} /> : null}
                {view.policyBindingAlg ? (
                  <Field label="policy binding" value={`${view.policyBindingAlg}${view.policyBindingHash ? ` · ${trunc(view.policyBindingHash, 8, 8)}` : ""}`} mono />
                ) : null}
                {view.rootSignatureAlg ? <Field label="integrity root" value={view.rootSignatureAlg} /> : null}
                {typeof view.segmentCount === "number" ? <Field label="segments" value={String(view.segmentCount)} /> : null}
                {view.schemaVersion ? <Field label="schema" value={`ZTDF ${view.schemaVersion}`} /> : null}
                {typeof view.totalBytes === "number" ? <Field label="envelope" value={`${view.totalBytes.toLocaleString()} bytes`} /> : null}
              </div>
              <pre className="chat__trust-manifest" aria-label="Raw ZTDF manifest JSON">{manifestJson}</pre>
              <div className="chat__trust-hint chat__trust-hint--seal">
                🔏 The fabric never sees what it carries — this manifest describes the envelope; the body ciphertext
                (<code>0.payload</code>) is <strong>not</strong> here in cleartext, and only the KAS releases the key
                per-reader, per-policy.
              </div>
            </>
          ) : (
            <div className={`chat__trust-note chat__trust-note--${view.kind}`}>{view.note}</div>
          )}
        </div>

        <div className="chat__trust-footer">
          Displayed classification: {classLabel(msg.contentClassification ?? msg.classification ?? "UNCLASSIFIED")} · all
          checks are client-side (no PDP/KAS decision path touched).
        </div>
      </div>
    </div>
  );
}
